import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { Logo } from '@/components/ui/Logo';

const AUTH_BASE_URL = '/api/user-identity';

type VerifyState = 'loading' | 'success' | 'error' | 'expired' | 'no-token';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerifyState>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token') || searchParams.get('code');

  useEffect(() => {
    if (!token) {
      setState('no-token');
      setMessage('No verification token provided. Please check your email for the verification link.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(
          `${AUTH_BASE_URL}/api/v1/auth/email/verify?token=${encodeURIComponent(token)}`,
          { method: 'GET' }
        );

        if (response.ok) {
          const data = await response.json();
          setState('success');
          setMessage(data?.message || 'Your email has been verified successfully!');
        } else {
          const data = await response.json().catch(() => null);
          const errorMsg = data?.message || '';

          if (errorMsg.toLowerCase().includes('expired')) {
            setState('expired');
            setMessage('This verification link has expired. Please request a new one.');
          } else if (errorMsg.toLowerCase().includes('already verified')) {
            setState('success');
            setMessage('Your email is already verified.');
          } else {
            setState('error');
            setMessage(errorMsg || 'Invalid verification link. Please request a new one.');
          }
        }
      } catch {
        setState('error');
        setMessage('Unable to verify your email. Please try again later.');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        <div className="flex justify-center">
          <Logo variant="full" className="h-10 w-auto" />
        </div>

        {state === 'loading' && (
          <div className="space-y-4">
            <BrandedLoading message="Verifying your email…" iconSize="md" />
            <p className="text-sm text-muted-foreground">
              Please wait while we verify your email address.
            </p>
          </div>
        )}

        {state === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-foreground">Email Verified!</h2>
            <p className="text-sm text-muted-foreground">{message}</p>
            <Link
              to="/coach"
              className="inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Go to Coach
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <svg className="h-8 w-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-foreground">Verification Failed</h2>
            <p className="text-sm text-muted-foreground">{message}</p>
            <Link
              to="/login"
              className="inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Go to Login
            </Link>
          </>
        )}

        {state === 'expired' && (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10">
                <svg className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-foreground">Link Expired</h2>
            <p className="text-sm text-muted-foreground">{message}</p>
            <Link
              to="/login"
              className="inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Log in to resend verification
            </Link>
          </>
        )}

        {state === 'no-token' && (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-foreground">No Verification Token</h2>
            <p className="text-sm text-muted-foreground">{message}</p>
            <Link
              to="/login"
              className="inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Go to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
